import { MessageSquare, PenLine } from 'lucide-react';
import { useEffect, useState } from 'react';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from 'components/ui/command';
import { Dialog, DialogContent } from 'components/ui/dialog';
import { Icon, IconName } from '../menu-icon/menu-icon';
import { groupConversations } from '../layout/hooks/useChatSidebar';
import { useChatStore } from '../../../state/store/chat/use-chat-store';
import { useNavigate } from 'react-router-dom';
import { useSidebar } from '../../ui/sidebar';
import { useTranslation } from 'react-i18next';
import { useConfig } from 'hooks/use-config';

export function ConversationSearch() {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const fetchSearchResults = useChatStore((state) => state.fetchSearchResults);
  const navigate = useNavigate();
  const { setOpenMobile, isMobile } = useSidebar();
  const { t } = useTranslation();
  const { configurationSettings } = useConfig();

  useEffect(() => {
    fetchSearchResults(search, configurationSettings);
  }, [search]);

  useEffect(() => {
    if (!open) {
      setSearch('');
    }
  }, [open]);

  const handleMobileSidebar = () => {
    setOpen(true);
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  // const flatConversationList : SidebarItem[] = [
  //     {
  //         "ConversationId": "9f053027-2266-447d-8f78-ff8b0b5c993b",
  //         "CreatedBy": "0cc48bd1-c73a-432f-99c9-0bd8d95bbc45",
  //         "TenantId": "cf18dc87904c4e1485639242cda4a026",
  //         "ConversationTitle": "Greeting and Introduction",
  //         "CreatedDate": "2025-05-15T15:56:48.836878+00:00",
  //         "LastUpdatedDate": "2025-05-15T15:56:48.836878+00:00",
  //         "Conversations": [],
  //     },
  //     {
  //         "CreatedBy": "0cc48bd1-c73a-432f-99c9-0bd8d95bbc45",
  //         "TenantId": "cf18dc87904c4e1485639242cda4a026",
  //         "ConversationId": "5eb303c5-21b7-4794-a6f1-dccd7a0cbab4",
  //         "ConversationTitle": "Greeting Initiation",
  //         "CreatedDate": "2025-05-15T13:24:48.611286+00:00",
  //         "LastUpdatedDate": "2025-05-15T13:26:09.421694+00:00",
  //         "Conversations": [],
  //     },
  //     {
  //         "ConversationId": "cb0acad4-73c6-4798-91b4-8c19caeb858c",
  //         "TenantId": "cf18dc87904c4e1485639242cda4a026",
  //         "CreatedBy": "0cc48bd1-c73a-432f-99c9-0bd8d95bbc45",
  //         "ConversationTitle": "Greeting Initiation",
  //         "CreatedDate": "2025-05-15T13:23:08.317790+00:00",
  //         "LastUpdatedDate": "2025-05-15T13:23:08.317790+00:00",
  //         "Conversations": [],
  //     },
  //     {
  //       "ConversationId": "cb0acad4-73c6-4798-91b4-8c19caeb8000",
  //       "TenantId": "cf18dc87904c4e1485639242cda4a026",
  //       "CreatedBy": "0cc48bd1-c73a-432f-99c9-0bd8d95bbc45",
  //       "ConversationTitle": "Qdrant for CrewAI Memory",
  //       "CreatedDate": "2025-05-05T13:23:08.317790+00:00",
  //       "LastUpdatedDate": "2025-05-05T13:23:08.317790+00:00",
  //       "Conversations": [],
  //     }
  //   ]
  const flatConversationList = useChatStore((state) => state.searchConversationsList);
  const groupedConversations = groupConversations(flatConversationList, t);

  return (
    <div className="flex justify-center items-center min-h-screen">
      <button
        onClick={handleMobileSidebar}
        className="ml-1 p-2 rounded-lg hover:bg-muted-foreground/10"
      >
        <Icon name={'Search' as IconName} size={20} strokeWidth={2.2} />
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          showOverlay={true}
          className="p-0 gap-0 w-[calc(100%-2rem)] max-w-[600px] sm:w-full sm:max-w-[45%] rounded-lg shadow-lg border mx-auto"
        >
          <Command className="rounded-lg">
            <CommandInput
              placeholder={`${t('SEARCH_CHATS')}...`}
              value={search}
              onValueChange={setSearch}
              className="flex-1 py-3 border-0 pr-4"
            />
            <CommandList className="h-[400px] overflow-auto py-2">
              {groupedConversations.length === 0 && (
                <CommandEmpty>{t('NO_RESULTS_FOUND')}</CommandEmpty>
              )}

              <CommandGroup>
                <CommandItem
                  className="flex items-center gap-2 px-4 py-3 cursor-pointer group/new-chat"
                  onSelect={() => {
                    console.log('New chat');
                    setOpen(false);
                    navigate('/chat');
                  }}
                >
                  <PenLine className="h-4 w-4 text-muted-foreground group-hover/new-chat:text-foreground" />
                  <span className="truncate text-sm group-hover/new-chat:text-foreground">
                    {t('NEW_CHAT')}
                  </span>
                </CommandItem>
              </CommandGroup>

              {groupedConversations.map((group) => (
                <CommandGroup key={group.id} heading={group.label}>
                  {group.items.map((chat) => (
                    <CommandItem
                      key={chat.ConversationId}
                      value={`${chat.ConversationTitle}-${chat.ConversationId}`}
                      className="flex items-center gap-2 px-4 py-3 cursor-pointer group/item"
                      data-conversation-id={chat.ConversationId}
                      onSelect={() => {
                        console.log(`Selected chat: ${chat.ConversationId}`);
                        setOpen(false);
                        navigate(`/chat/${chat.ConversationId}`);
                      }}
                    >
                      <MessageSquare className="h-4 w-4 text-muted-foreground group-hover/item:text-foreground" />
                      <span className="truncate text-sm group-hover/item:text-foreground">
                        {chat.ConversationTitle}
                      </span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              ))}
            </CommandList>
          </Command>
        </DialogContent>
      </Dialog>
    </div>
  );
}
